import React, { useEffect } from "react"
import { useFormContext } from "react-hook-form"
// react-select is the engine behind the deprecated FormSingleSelect (via @carrotfertility/carotene);
// we wrap its default Option to add a value-keyed test-id without changing its rendering.
import { components as reactSelectComponents } from "react-select"
import { useStateMachine } from "#/components/context/stateMachine/StateMachineContext"
import { FormSingleSelect } from "#/components/views/atoms/forms/FormSingleSelect"
// eslint-disable-next-line no-restricted-imports
import { SelectProps } from "@carrotfertility/carotene" // please do not import from @carrotfertility/carotene after eslint rule added on 3/20/25
import { useIntl } from "react-intl"
import { Box, Button, IconButton, BackIcon, ForwardIcon } from "@carrotfertility/carotene-core"
import { FormRadioGroup } from "@carrotfertility/carotene-core-x"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { NotAllowedWhilePosing } from "#/components/cmd-enrollment/shared/NotAllowedWhilePosing"

/**
 * Stable test-id contract for the benefit-enrollment flow.
 *
 * These ids let the test suite drive and assert the flow by *what a control is*
 * (continue, back, the option whose branch value is X) rather than by translated
 * copy or the underlying widget shape. A refactor (e.g. migrating these deprecated
 * selects to carotene-core-x) must keep the same ids on the functionally-equivalent
 * controls so the journey/decision tests keep passing without edits.
 */
export const ENROLLMENT_TEST_IDS = {
  continue: "enrollment-continue",
  back: "enrollment-back",
  /** Keyed on the option's emitted branch value (e.g. "EMPLOYER", "true", "MEMBER_ONLY"). */
  option: (value: unknown): string => `enrollment-option-${String(value)}`,
  /** Per-screen identity, keyed on the state-machine step name. */
  step: (stepName: string): string => `enrollment-step-${stepName}`
}

/**
 * react-select's default Option, augmented with a value-keyed test-id. Wrapping the
 * default component (rather than replacing it) preserves all of react-select's
 * styling and selection behavior — only `data-testid` is added.
 */
function EnrollmentSelectOption(props: any): JSX.Element {
  return (
    <reactSelectComponents.Option
      {...props}
      innerProps={{ ...props.innerProps, "data-testid": ENROLLMENT_TEST_IDS.option(props.data?.value) }}
    />
  )
}

interface BenefitEnrollmentFlowModalBackButtonProps {
  handleGoBack?: () => void
}
export function BenefitEnrollmentFlowModalBackButton({
  handleGoBack
}: BenefitEnrollmentFlowModalBackButtonProps): JSX.Element {
  const { gotoPreviousStep } = useStateMachine()
  const intl = useIntl()
  return (
    <IconButton
      size="medium"
      onClick={handleGoBack || gotoPreviousStep}
      aria-label={intl.formatMessage({ defaultMessage: "Back" })}
      color="secondary"
      data-testid={ENROLLMENT_TEST_IDS.back}
    >
      <BackIcon color="secondary" />
    </IconButton>
  )
}

export function BenefitEnrollmentModalContinueButton({ onClick }: { onClick: () => void }): JSX.Element {
  const intl = useIntl()
  const continueText = intl.formatMessage({ defaultMessage: "Continue" })
  const { isPosingAsMember } = useCurrentUser()
  return (
    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
      <Button
        disabled={isPosingAsMember}
        size="medium"
        onClick={onClick}
        aria-label={continueText}
        sx={{ textWrap: "no-wrap" }}
        color="secondary"
        variant="outlined"
        endIcon={<ForwardIcon />}
        data-testid={ENROLLMENT_TEST_IDS.continue}
      >
        {continueText}
      </Button>
      {isPosingAsMember && <NotAllowedWhilePosing />}
    </Box>
  )
}

interface BenefitEnrollmentModalFormSelectProps extends Omit<SelectProps, "defaultValue"> {
  defaultValue?: string | boolean
}
export function BenefitEnrollmentModalFormSelect({
  defaultValue = null,
  name,
  options,
  ...otherProps
}: BenefitEnrollmentModalFormSelectProps): JSX.Element {
  const { setValue } = useFormContext()
  const value = defaultValue !== null ? String(defaultValue) : null
  const intl = useIntl()
  useEffect(() => {
    setValue(
      name,
      options.find(({ value: optionValue }) => String(optionValue) === value)
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps -- See https://carrotfertility.atlassian.net/wiki/spaces/PE/pages/2050295461/Remove+Build+Warnings#react-hooks%2Fexhaustive-deps
  }, [])
  return (
    <FormSingleSelect
      optionComponent={EnrollmentSelectOption}
      rules={{ required: intl.formatMessage({ defaultMessage: "Required" }) }}
      {...{ ...otherProps, name, options }}
    />
  )
}

interface BenefitEnrollmentModalFormRadioProps {
  name: string
  options: { label: string; value: string }[]
}
export function BenefitEnrollmentModalFormRadio({ name, options }: BenefitEnrollmentModalFormRadioProps): JSX.Element {
  const intl = useIntl()
  return (
    <FormRadioGroup
      name={name}
      options={options}
      controllerProps={{ rules: { required: intl.formatMessage({ defaultMessage: "Required" }) } }}
    />
  )
}
